import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Plus, Copy, Loader2, Trash2, Ban, ShieldCheck, Info } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [freshKey, setFreshKey] = useState(null);   // {id, key, prefix, name}
  const [confirmRevoke, setConfirmRevoke] = useState(null); // key row
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data.api_keys || []);
      setScopes(data.valid_scopes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setCreatingBusy(true);
    try {
      const { data } = await api.post("/api-keys", {
        name: name.trim(),
        scopes: ["leads:create"],
      });
      setCreating(false);
      setName("");
      setFreshKey(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create key");
    } finally {
      setCreatingBusy(false);
    }
  };

  const revoke = async (row) => {
    setConfirmRevoke(null);
    try {
      await api.post(`/api-keys/${row.id}/revoke`);
      toast.success("API key revoked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke");
    }
  };

  const remove = async (row) => {
    setConfirmDelete(null);
    try {
      await api.delete(`/api-keys/${row.id}`);
      toast.success("API key deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — highlight and copy manually.");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6 card-crisp">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp mb-6" data-testid="api-keys-panel">
      <div className="p-6 border-b border-zinc-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#FF5A00]" /> API keys
          </h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            Server-to-server keys for external websites (e.g. Rock River Home &amp; RV) to submit inbound leads.
            Keys are revocable and scoped — they can only create leads, not read or edit CRM data.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
          data-testid="api-keys-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New key
        </Button>
      </div>

      {keys.length === 0 ? (
        <div className="p-8 text-center text-zinc-400 text-sm" data-testid="api-keys-empty">
          No API keys yet. Create one to let an external site submit leads securely.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 border-b border-zinc-100">
            <tr>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left">Prefix</th>
              <th className="text-left">Scopes</th>
              <th className="text-left">Last used</th>
              <th className="text-left">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-zinc-100 last:border-0" data-testid={`api-key-${k.id}`}>
                <td className="px-6 py-3">
                  <div className="font-medium">{k.name}</div>
                  <div className="text-[11px] text-zinc-500">by {k.created_by_email} · {fmt(k.created_at)}</div>
                </td>
                <td className="font-mono-accent text-xs text-zinc-700">{k.prefix}…</td>
                <td className="text-xs">
                  {(k.scopes || []).map((s) => (
                    <span key={s} className="inline-flex items-center bg-zinc-100 text-zinc-700 rounded px-1.5 py-0.5 mr-1 font-mono-accent text-[10px]">
                      {s}
                    </span>
                  ))}
                </td>
                <td className="text-xs text-zinc-500">
                  {k.last_used_at ? <>{fmt(k.last_used_at)} · <span className="text-zinc-700">{k.use_count} uses</span></> : "—"}
                </td>
                <td>
                  {k.revoked_at ? (
                    <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-medium">
                      <Ban className="w-3 h-3" /> Revoked
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                      <ShieldCheck className="w-3 h-3" /> Active
                    </span>
                  )}
                </td>
                <td className="text-right px-6 py-3">
                  {!k.revoked_at && (
                    <button
                      onClick={() => setConfirmRevoke(k)}
                      className="text-xs inline-flex items-center gap-1 text-amber-700 hover:bg-amber-50 px-2 py-1 rounded mr-1"
                      data-testid={`api-key-revoke-${k.id}`}
                    >
                      <Ban className="w-3 h-3" /> Revoke
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmDelete(k)}
                    className="text-xs inline-flex items-center gap-1 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded"
                    data-testid={`api-key-delete-${k.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="api-keys-create-dialog">
          <DialogHeader>
            <DialogTitle>New API key</DialogTitle>
            <DialogDescription>
              Name identifies where the key is deployed. The key value is shown <strong>once</strong> right after
              creation — copy it into the external site's environment before closing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-[10px] uppercase font-mono-accent text-zinc-500">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Rock River Home & RV — website"
              data-testid="api-keys-name-input"
            />
            <div className="text-xs text-zinc-500 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5" />
              This key can only <span className="font-semibold">create inbound leads</span>. It cannot read,
              edit, or delete any CRM data. Scope: <code>leads:create</code>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              onClick={create}
              disabled={creatingBusy}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              data-testid="api-keys-save-btn"
            >
              {creatingBusy ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating…</>) : "Create key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fresh key reveal — shows the raw key ONCE */}
      <Dialog open={!!freshKey} onOpenChange={(v) => !v && setFreshKey(null)}>
        <DialogContent data-testid="api-keys-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" /> Copy your key now
            </DialogTitle>
            <DialogDescription>
              This is the <strong>only time</strong> the full key is visible. Store it in the external site's
              environment variables (e.g., <code>LEADFORGE_API_KEY</code>). If you lose it, revoke and create a new one.
            </DialogDescription>
          </DialogHeader>
          {freshKey && (
            <div className="space-y-3">
              <div className="rounded border border-zinc-200 bg-zinc-950 text-emerald-300 font-mono text-sm p-3 break-all"
                   data-testid="api-keys-reveal-value">
                {freshKey.key}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => copy(freshKey.key, "API key")}
                        data-testid="api-keys-reveal-copy-key">
                  <Copy className="w-4 h-4 mr-1" /> Copy key
                </Button>
                <Button variant="outline" onClick={() => copy(
                  `curl -X POST "${window.location.origin}/api/inbound/leads" -H "Authorization: Bearer ${freshKey.key}" -H "Content-Type: application/json" -d '{"customer_name":"Test Customer","email":"test@example.com","phone":"555-0100"}'`,
                  "cURL example",
                )} data-testid="api-keys-reveal-copy-curl">
                  <Copy className="w-4 h-4 mr-1" /> Copy cURL
                </Button>
              </div>
              <div className="text-[11px] text-zinc-500 border border-zinc-100 rounded p-2 bg-zinc-50">
                <div className="uppercase font-mono-accent text-[9px] tracking-widest text-zinc-500 mb-1">Endpoint</div>
                <code className="text-zinc-700">POST {typeof window !== "undefined" ? window.location.origin : ""}/api/inbound/leads</code>
                <div className="mt-1">Send either <code>Authorization: Bearer &lt;key&gt;</code> or <code>X-API-Key: &lt;key&gt;</code>.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setFreshKey(null)} data-testid="api-keys-reveal-close">
              I've saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog open={!!confirmRevoke} onOpenChange={(v) => !v && setConfirmRevoke(null)}>
        <AlertDialogContent data-testid="api-keys-revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this key?</AlertDialogTitle>
            <AlertDialogDescription>
              Requests using this key will immediately fail with 401. Existing leads already created are
              unaffected. This is reversible only by creating a new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revoke(confirmRevoke)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="api-keys-revoke-confirm-btn"
            >
              Yes, revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="api-keys-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this key permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the record entirely and cannot be undone. Prefer <em>Revoke</em> if you want to
              preserve an audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(confirmDelete)}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="api-keys-delete-confirm-btn"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
